import { type ReactNode, useRef, useState } from "react";
import { Button, Dialog, Row, Spinner, Text, useToast } from "@hightouch/htui";
import { useQueryClient } from "react-query";
import { MAX_PAYLOAD_SIZE } from "src/components/llm/utils/file-processing";
import {
  AgenticSettingsEntityType,
  useCreateAgenticSettingsEntityMutation,
  useEntityCitationsQuery,
  useGetCurrentAgenticSettingsEntitiesQuery,
} from "src/graphql/types";
import { captureException } from "src/lib/monitoring";
import {
  fileNameWithoutExtension,
  isMarkdownFile,
} from "src/pages/context-hub/components/knowledge-detail/markdown-file";
import { useBrandProfileFilter } from "src/pages/context-hub/components/knowledge-list/use-brand-profile-filter";
import { fileToString } from "src/ui/file/file-uploader";
import { CONTEXT_SURFACE } from "./surface";
import { useUploadContextFile } from "./use-upload-context-file";
import { buildCreateInput, emptyFormValues } from "./utils";

type UploadFileEntity = {
  /** Opens the file picker. */
  open: () => void;
  /** The hidden file input. Render it on the page. */
  input: ReactNode;
};

/**
 * The Files create menu's "Upload file": the picked file becomes a file, then
 * opens. A Markdown file becomes a document; any other an uploaded file.
 */
export function useUploadFileEntity({
  onUploaded,
}: {
  onUploaded: (id: string) => void;
}): UploadFileEntity {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploadingFile, setUploadingFile] = useState<File | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  // Uploaded while the list is scoped to brand profiles, it's assigned to them.
  const { brandProfileIds } = useBrandProfileFilter();
  const uploadContextFile = useUploadContextFile();
  // Refreshes the lists itself once the citations are in too.
  const { mutateAsync: createEntity } = useCreateAgenticSettingsEntityMutation({
    onSuccess: () => {},
  });

  // Cancelling stops the upload up to the create mutation; once that's sent
  // the file exists, so it's listed, just not opened.
  const createFromFile = async (
    file: File,
    signal: AbortSignal,
  ): Promise<string> => {
    const base = {
      ...emptyFormValues(CONTEXT_SURFACE.forcedContextType ?? null),
      // Starts as a draft, like a file written on `/new`.
      isActive: false,
      brandProfileIds: brandProfileIds ?? [],
    };
    if (isMarkdownFile(file)) {
      const markdown = await fileToString(file);
      signal.throwIfAborted();
      const result = await createEntity({
        input: buildCreateInput(
          { ...base, name: fileNameWithoutExtension(file.name), markdown },
          null,
        ),
      });
      return result.createAgenticSettingsEntity.id;
    }
    const stagedFile = {
      filename: file.name,
      filetype: file.type || "application/octet-stream",
      filesize: file.size,
      file,
    };
    const s3Key = await uploadContextFile(stagedFile, signal);
    signal.throwIfAborted();
    const result = await createEntity({
      input: buildCreateInput(
        {
          ...base,
          name: file.name,
          type: AgenticSettingsEntityType.ContextFile,
          stagedFile,
        },
        s3Key,
      ),
    });
    return result.createAgenticSettingsEntity.id;
  };

  const handleFile = async (file: File) => {
    if (file.size > MAX_PAYLOAD_SIZE) {
      toast({
        id: "upload-context-file",
        variant: "error",
        title: "File too large",
        message: "Files must be 50MB or smaller.",
      });
      return;
    }
    const controller = new AbortController();
    abortRef.current = controller;
    setUploadingFile(file);
    try {
      const id = await createFromFile(file, controller.signal);
      await Promise.all([
        queryClient.invalidateQueries(
          useGetCurrentAgenticSettingsEntitiesQuery.getKey(),
        ),
        queryClient.invalidateQueries(
          useEntityCitationsQuery.getKey({ entityIds: [] }).slice(0, 1),
        ),
      ]);
      if (!controller.signal.aborted) onUploaded(id);
    } catch (error) {
      // The user cancelled; there's nothing to report.
      if (controller.signal.aborted) return;
      captureException(error);
      toast({
        id: "upload-context-file",
        variant: "error",
        title: `Couldn't upload ${file.name}`,
        message: error instanceof Error ? error.message : "Unknown error",
      });
    } finally {
      // A cancelled upload can still be unwinding after the next one starts;
      // it mustn't clear that one's dialog or cancel handle.
      if (abortRef.current === controller) {
        abortRef.current = null;
        setUploadingFile(null);
      }
    }
  };

  const cancel = () => {
    abortRef.current?.abort();
    abortRef.current = null;
    setUploadingFile(null);
  };

  return {
    open: () => inputRef.current?.click(),
    input: (
      <>
        <Dialog
          isOpen={uploadingFile !== null}
          // Escape cancels too, so the dialog never traps the user.
          onClose={cancel}
          variant="form"
          width="sm"
          title="Uploading file"
          actions={
            <Button variant="secondary" onClick={cancel}>
              Cancel
            </Button>
          }
        >
          <Row align="center" gap={3}>
            <Spinner size="sm" />
            <Text>Uploading {uploadingFile?.name}…</Text>
          </Row>
        </Dialog>
        <input
          ref={inputRef}
          type="file"
          hidden
          aria-label="Upload file"
          onChange={(event) => {
            const file = event.target.files?.[0];
            // Cleared so picking the same file again still fires a change.
            event.target.value = "";
            if (file) void handleFile(file);
          }}
        />
      </>
    ),
  };
}
